import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import { useGetProjects } from "src/domains/projects-list/project-query/projects.queries";
import {
  resolveQueryRenderState,
  type QueryRenderResult,
} from "src/domains/shared/utils/queryRenderState";

export interface ProjectOption {
  label: string;
  value: string;
}

/**
 * 프로젝트 히스토리 패널의 "프로젝트 선택" 상태 훅.
 *
 * - 옵션 목록: `useGetProjects()` 을 인자 없이(소속 전체) 호출한다 — 히스토리는 멤버면 조회
 *   가능하므로 권한 관리 전용 필터인 `{isOwn:true}`(useUserManagement) 와 다르다(design.md D6).
 * - 기본 선택: 라우터 `query.projectId` 후보가 옵션에 있으면 그 값을, 없으면 첫 옵션을 선택한다
 *   (useUserManagement 의 기본 선택 effect 미러 — 옵션 로드 후 effect 로만 채운다).
 */
export const useHistoryProjectSelection = () => {
  const router = useRouter();
  const routeProjectIdRaw = router.query.projectId;
  const routeProjectIdCandidate = Array.isArray(routeProjectIdRaw)
    ? routeProjectIdRaw[0]
    : routeProjectIdRaw;

  const [selectedProject, setSelectedProject] = useState("");

  const {
    data: projects,
    status: projectsStatus,
    fetchStatus: projectsFetchStatus,
    refetch: refetchProjects,
  } = useGetProjects();

  // 봉투 실패는 경계에서 오류로 승격되므로 여기서 다시 검사하지 않는다 —
  // 실패는 `data === undefined` + `isError` 로 나타나고, 옵션 0건과 구별된다.
  const projectOptions: ProjectOption[] = useMemo(
    () =>
      (projects ?? []).map((project) => ({
        label: project.title,
        value: project.id.toString(),
      })),
    [projects]
  );

  // 프로젝트 옵션이 로드된 후에만 기본 선택을 채운다 — 로드 전엔 selectedProject 가 빈 문자열로
  // 남아 enabled(!!projectId) 게이팅에 걸려 히스토리 조회가 나가지 않는다.
  useEffect(() => {
    if (projectOptions.length === 0) return;
    if (projectOptions.some((option) => option.value === selectedProject)) {
      return;
    }

    const candidateOption = routeProjectIdCandidate
      ? projectOptions.find(
          (option) => option.value === routeProjectIdCandidate
        )
      : undefined;
    setSelectedProject(candidateOption?.value ?? projectOptions[0].value);
  }, [projectOptions, selectedProject, routeProjectIdCandidate]);

  // 갈래를 여기서 한 번만 내려 패널(ProjectHistory)과 필터 바(HistoryFilterBar)가 같은
  // 판정을 본다 — 조회 상태와 항목 수를 소비처가 각자 조합하면 한쪽은 오류를, 다른 쪽은
  // "조회 중" 을 보여주는 어긋남이 생긴다. 그래서 쿼리 상태 원자료(`isLoading`·`isFetching`·
  // `isError`·`status`)는 반환하지 않고 `projectListRenderState` 와 `refetchProjects` 만
  // 판정 표면으로 내보낸다.
  // 소속 프로젝트 0개는 정상 상태다 — 항목 수로 2단 가드를 가르면 이미 확인한 "0건" 을
  // 배경 재조회 실패 한 번에 오류 화면이 덮는다(`refetchOnWindowFocus: true`).
  const projectListRenderState: QueryRenderResult = resolveQueryRenderState({
    status: projectsStatus,
    fetchStatus: projectsFetchStatus,
    itemCount: projectOptions.length,
    hasData: projects !== undefined,
  });

  return {
    projectOptions,
    selectedProject,
    setSelectedProject,
    projectListRenderState,
    refetchProjects,
  };
};
